import { cn } from "cn";
import type * as React from "react";
import { useState } from "react";
import {
  BwCloseCircleFill,
  BwEyeHideLine,
  BwEyeOpenLine,
} from "@/components/ui/icons";

type InputProps = Omit<React.ComponentProps<"input">, "prefix"> & {
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  onClear?: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  trigger?: (name?: any) => void;
  clearable?: boolean;
  prefixClassName?: string;
  suffixClassName?: string;
};

function Input({
  className,
  type,
  prefix,
  suffix,
  onClear,
  trigger,
  clearable = true,
  prefixClassName,
  suffixClassName,
  ...props
}: InputProps) {
  const [inputType, setInputType] = useState(type);
  const handleClear = (e: React.MouseEvent<SVGSVGElement>) => {
    e.stopPropagation();
    onClear?.();
    props.onChange?.({
      target: { value: "" },
    } as React.ChangeEvent<HTMLInputElement>);
    trigger?.(props.name);
    if (props.ref && typeof props.ref === "object" && "current" in props.ref) {
      props.ref.current?.focus();
    }
  };
  return (
    <div
      className={cn(
        /* sorted */
        "group flex items-center gap-2",
        "h-10 min-w-0",
        "px-3 py-2.5",
        "outline outline-outline-dark-low rounded-lg",
        "transition-[color,box-shadow]",
        "focus-within:outline-outline-dark-max focus-within:outline-[1.5px]",
        "has-aria-invalid:outline-outline-negative-max",
        "has-[[disabled]]:cursor-not-allowed has-[[disabled]]:bg-bg-dark-low has-[[disabled]]:opacity-50",
        className,
      )}
    >
      <div className="flex items-center min-w-0 grow">
        {prefix && (
          <div
            className={cn(
              /* sorted */
              "shrink-0",
              "pr-1",
              "text-sm text-text-dark-high",
              prefixClassName,
            )}
          >
            {prefix}
          </div>
        )}
        <input
          ref={props.ref}
          type={inputType}
          data-slot="input"
          className={cn(
            /* sorted */
            "w-full grow",
            "font-normal text-sm leading-5 text-text-dark-max",
            "bg-transparent outline-none",
            "placeholder:text-text-dark-min",
            "focus-visible:ring-0 focus-visible:border-none",
            "disabled:pointer-events-none disabled:cursor-not-allowed disabled:text-text-dark-min",
            "file:bg-bg-dark-min no-spinner",
            //"selection:bg-bg-primary-min"
          )}
          {...props}
          onChange={(e) => {
            props.onChange?.(e);
            if (props?.["aria-invalid"]) {
              trigger?.(props.name);
            }
          }}
        />
      </div>
      {type === "password" &&
        (inputType === "password" ? (
          <BwEyeHideLine
            size={16}
            className="cursor-pointer text-text-dark-min shrink-0"
            onClick={() => setInputType("text")}
          />
        ) : (
          <BwEyeOpenLine
            size={16}
            className="cursor-pointer text-text-dark-min shrink-0"
            onClick={() => setInputType("password")}
          />
        ))}
      {!!props.value && type !== "password" && clearable && (
        <BwCloseCircleFill
          size={16}
          className="md:opacity-0 cursor-pointer text-text-dark-min group-hover:opacity-100 shrink-0"
          onClick={handleClear}
        />
      )}
      {suffix && (
        <div className={cn("shrink-0", suffixClassName)}>{suffix}</div>
      )}
    </div>
  );
}

export { Input };
